import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { ClientsService } from "../../services/clients.service";
import { GetClientsDto } from "../../dto/clients.dto";
import { PaginationDto } from "src/app/core/dto/pagination.dto";
import { ErrorMessagesService } from "src/app/shared/messages/services/error-messages.service";
import { AuthService } from "src/app/core/services/auth/auth.service";

@Component({
  selector: "app-client-list",
  templateUrl: "./clients-list.component.html",
})
export class ClientListComponent implements OnInit, OnDestroy {
  clients: GetClientsDto[] = [];
  paginacao: PaginationDto = {
    paginaAtual: 1,
    quantidadePorPagina: 10,
    totalDeItens: 0,
    ultimaPagina: 0,
  };
  filterForm: FormGroup;
  private filterSubscription: Subscription = new Subscription();
  private clientsSubscription: Subscription = new Subscription();

  error = "";
  erro403 = false;

  //permissoes
  podeEditar = false;
  podeVer = false

  // Flag para evitar loops de paginação
  private atualizandoProgramaticamente = false;

  constructor(
    public clientService: ClientsService,
    private fb: FormBuilder,
    private errorMessagesService: ErrorMessagesService,
    private authService: AuthService
  ) {
    this.filterForm = this.fb.group({
      "Paginacao.PaginaAtual": 1,
      "Paginacao.QuantidadePorPagina": 10,
      Nome: [""],
      Contato: [""],
      Status: [""],
      Feiras: [""],
      Habilitado: [""],
      Metro: [""],
    });
  }

  ngOnInit(): void {

    this.permissaoAdicionarUsuario();

    const filters = this.clientService.filters;
    this.filterForm.patchValue({
      "Paginacao.PaginaAtual": filters["Paginacao.PaginaAtual"],
      "Paginacao.QuantidadePorPagina": filters["Paginacao.QuantidadePorPagina"],
      Nome: filters.Nome,
      Contato: filters.Contato,
      Feiras: filters.Feiras,
      Status: filters.Status,
      Habilitado: filters.Habilitado,
      Metro: filters.Metro,
    });

    this.clientsSubscription = this.clientService.getClients().subscribe({
      next: (response) => {
        this.clients = response.clientes;
        
        // Atualiza paginação sem disparar eventos
        this.atualizandoProgramaticamente = true;
        this.paginacao.paginaAtual = response.paginacao.paginaAtual;
        this.paginacao.quantidadePorPagina = response.paginacao.quantidadePorPagina;
        this.paginacao.totalDeItens = response.paginacao.totalDeItens;
        this.paginacao.ultimaPagina = response.paginacao.ultimaPagina;
        
        // Reset flag após um pequeno delay
        setTimeout(() => {
          this.atualizandoProgramaticamente = false;
        }, 100);
      },
      error: (error) => {
        if (error.status == 403) {
          this.erro403 = true;
          return;
        } 
        this.error = this.errorMessagesService.getMessage('ERR_0001');
        console.error(error);
        this.atualizandoProgramaticamente = false;
      },
    });

    this.filterSubscription = this.filterForm.valueChanges.subscribe(
      (filters) => {
        this.clientService.setFilters({ ...filters });
      },
    );
  }

  onPageChange(page: number): void {
    // Evita loop: só atualiza se não estiver atualizando programaticamente
    if (!this.atualizandoProgramaticamente) {
      this.clientService.setFilters({ "Paginacao.PaginaAtual": page });
    }
  }

  ngOnDestroy(): void {
    // Cancelar a inscrição quando o componente for destruído
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    if (this.clientsSubscription) {
      this.clientsSubscription.unsubscribe();
    }
  }

  getFeirasNomes(feiras: GetClientsDto["feiras"]): string {
    return feiras.map((feira) => feira.nome).join(", ");
  }

  getFeirasMetro(feiras: GetClientsDto["feiras"]): string {
    return feiras.map((feira) => feira.metroQuadrado).join(", ");
  }

  permissaoAdicionarUsuario(): void {
    this.podeEditar = this.authService.verificarPermissao('Call_Contatos', 'Editar')
  }

  getStatusBadgeClass(status: string): string {
    if (!status || status === 'temporario') {
      return 'bg-secondary-subtle text-secondary';
    }
    
    // O status vem no formato "nome|cor" onde cor pode ser "bg-success-subtle" ou similar
    const [nome, cor] = status.split('|');
    
    if (cor) {
      // Se a cor já está no formato "bg-{cor}-subtle", extrair a cor base e adicionar text-{cor}
      const corMatch = cor.match(/bg-(\w+)-subtle/);
      if (corMatch && corMatch[1]) {
        const corBase = corMatch[1]; // ex: "success", "warning", "danger", etc.
        return `bg-${corBase}-subtle text-${corBase}`;
      }
      // Se não está no formato esperado, tentar mapear manualmente
      const corLower = cor.toLowerCase().trim();
      if (corLower.includes('success') || corLower.includes('verde')) {
        return 'bg-success-subtle text-success';
      }
      if (corLower.includes('warning') || corLower.includes('amarelo') || corLower.includes('laranja')) {
        return 'bg-warning-subtle text-warning';
      }
      if (corLower.includes('danger') || corLower.includes('vermelho')) {
        return 'bg-danger-subtle text-danger';
      }
      if (corLower.includes('info') || corLower.includes('azul')) {
        return 'bg-info-subtle text-info';
      }
      if (corLower.includes('primary')) {
        return 'bg-primary-subtle text-primary';
      }
    }
    
    // Se não encontrou cor, usar padrão secundário
    return 'bg-secondary-subtle text-secondary';
  }

  getStatusLabel(status: string): string {
    if (!status || status === 'temporario') {
      return '';
    }
    
    // O status vem no formato "nome|cor"
    const [nome] = status.split('|');
    return nome || '';
  }
}
